/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext } from "react";
import { KTSVG } from "../../../helpers";
import {
  TWPContext,
  clearSelectedFiltersFromSession,
  clearAllFiltersFromSession,
} from "../../widgets/tables/context/TWPContext";

export default function ProcessFilterDropdown() {
  const context = useContext(TWPContext);

  const handleTemplateFilterToggle = (templateName: string) => {
    const allTemplateNames = context.state.templates.map(t => t.data?.name || "").filter(name => name);
    const currentExcluded = context.state.selectedTemplateFilters;
    let newExcluded: string[];
    
    if (currentExcluded.includes(templateName)) {
      // Template is currently excluded, so include it back (remove from excluded list)
      newExcluded = currentExcluded.filter(filter => filter !== templateName);
    } else {
      // Template is currently included, so exclude it (add to excluded list)
      newExcluded = [...currentExcluded, templateName];
    }
    
    // Calculate which templates should be included (not excluded)
    const includedTemplates = allTemplateNames.filter(name => !newExcluded.includes(name));
    
    // Update the excluded filters state (for UI display)
    context.dispatch({
      type: "SET_SELECTED_TEMPLATE_FILTERS",
      payload: newExcluded,
    });
    
    // Update the paging - send included templates as filter2 (inclusion logic)
    context.dispatch({
      type: "SET_PAGE",
      payload: {
        take: context.state.itemsPerPage,
        skip: 0,
        orderBy: context.state.paging.orderBy,
        order: context.state.paging.order,
        // Preserve existing completion status filters (filter/filterBy)
        filter: context.state.paging.filter,
        filterBy: context.state.paging.filterBy,
        // Set template filters using filter2/filterBy2 as included templates
        filter2: includedTemplates.length > 0 && includedTemplates.length < allTemplateNames.length ? includedTemplates : undefined,
        filterBy2: includedTemplates.length > 0 && includedTemplates.length < allTemplateNames.length ? "data.template" : undefined,
      },
    });
  };

  const handleSelectAllTemplates = () => {
    // Clear all template filters and session storage, but preserve completion status filters
    clearSelectedFiltersFromSession();
    
    context.dispatch({
      type: "SET_SELECTED_TEMPLATE_FILTERS",
      payload: [],
    });
    
    context.dispatch({
      type: "SET_PAGE",
      payload: {
        take: context.state.itemsPerPage,
        skip: 0,
        orderBy: context.state.paging.orderBy,
        order: context.state.paging.order,
        // Preserve existing completion status filters (filter/filterBy)
        filter: context.state.paging.filter,
        filterBy: context.state.paging.filterBy,
        // Clear template filters (filter2/filterBy2)
        filter2: undefined,
        filterBy2: undefined,
      },
    });
  };

  const handleAllProcessesClick = () => {
    // Clear all filters from session when selecting "All Processes"
    clearAllFiltersFromSession();
    
    context.dispatch({
      type: "SET_SELECTED_TEMPLATE_FILTERS",
      payload: [],
    });
    
    context.dispatch({
      type: "SET_PAGE",
      payload: {
        take: context.state.itemsPerPage,
        skip: 0,
        // Clear completion status filters (filter/filterBy)
        filterBy: undefined,
        filter: undefined,
        // Clear template filters (filter2/filterBy2)
        filter2: undefined,
        filterBy2: undefined,
        order: context.state.paging.order,
        orderBy: context.state.paging.orderBy,
      },
    });
  };

  const handleCompletionStatusFilter = (completionStatus: string) => {
    // Don't clear template filters when using completion status filters - they should work together
    context.dispatch({
      type: "SET_PAGE",
      payload: {
        take: context.state.itemsPerPage,
        skip: 0,
        // Set completion status filter (filter/filterBy)
        filterBy: "data.content.completed",
        filter: completionStatus,
        // Preserve template filters (filter2/filterBy2)
        filter2: context.state.paging.filter2,
        filterBy2: context.state.paging.filterBy2,
        order: context.state.paging.order,
        orderBy: context.state.paging.orderBy,
      },
    });
  };

  const handleDeselectAllTemplates = () => {
    const allTemplateNames = context.state.templates.map(t => t.data?.name || "").filter(name => name);
    
    // Exclude all templates
    context.dispatch({
      type: "SET_SELECTED_TEMPLATE_FILTERS",
      payload: allTemplateNames,
    });
    
    // Update the paging - no templates included (empty filter2)
    context.dispatch({
      type: "SET_PAGE",
      payload: {
        take: context.state.itemsPerPage,
        skip: 0,
        orderBy: context.state.paging.orderBy,
        order: context.state.paging.order,
        // Preserve existing completion status filters (filter/filterBy)
        filter: context.state.paging.filter,
        filterBy: context.state.paging.filterBy,
        // Set empty filter2 since no templates are included
        filter2: [],
        filterBy2: "data.template",
      },
    });
  };

  // Calculate template filter subtitle
  const allTemplateNames = context.state.templates.map(t => t.data?.name || "").filter(name => name);
  const excludedTemplates = context.state.selectedTemplateFilters;
  const includedTemplates = allTemplateNames.filter(name => !excludedTemplates.includes(name));
  
  const templateSubtitle = excludedTemplates.length === 0 
    ? "Alle Templates angezeigt" 
    : `${includedTemplates.length} Template(s) angezeigt (Session-persistent)`;

  return (
    <div>
      <button
        type="button"
        className="btn btn-sm btn-icon btn-color-primary btn-active-light-primary position-relative"
        data-kt-menu-trigger="click"
        data-kt-menu-placement="bottom-end"
        data-kt-menu-flip="top-end"
      >
        <KTSVG
          path="/media/icons/duotone/Layout/Layout-4-blocks-2.svg"
          className="svg-icon-1"
        />
        {excludedTemplates.length > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle badge badge-sm bg-primary rounded-pill">
            {includedTemplates.length}
            <span className="visually-hidden">Selected templates</span>
          </span>
        )}
      </button>
      <div
        className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-bold w-200px"
        data-kt-menu="true"
      >
        <div className="menu-item px-3">
          <div className="menu-content fs-6 text-dark fw-bolder px-3 py-4">
            Filtrieren & Sortieren
          </div>
          {context.state.selectedTemplateFilters.length > 0 ? (
            <div className="menu-content fs-7 text-muted px-3 pb-2">
              {context.state.selectedTemplateFilters.length} Template(s) ausgeschlossen (Session-persistent)
            </div>
          ) : (
            <div className="menu-content fs-7 text-muted px-3 pb-2">
              {templateSubtitle}
            </div>
          )}
        </div>

        <div className="separator mb-3 opacity-75"></div>

        <div className="menu-item px-3">
          <div className="d-flex flex-row align-items-center gap-3">
            <i
              className="las la-check"
              hidden={
                context.state.paging.filter !== undefined ||
                context.state.paging.filterBy !== undefined ||
                context.state.paging.filter2 !== undefined ||
                context.state.paging.filterBy2 !== undefined
              }
            ></i>
            <a
              href="#"
              className="menu-link px-3"
              onClick={handleAllProcessesClick}
            >
              Alle Prozesse
            </a>
          </div>
        </div>

        <div className="menu-item px-3">
          <div className="d-flex flex-row align-items-center gap-3">
            <i
              className="las la-check"
              hidden={
                context.state.paging.filter !== "true" ||
                context.state.paging.filterBy !== "data.content.completed"
              }
            ></i>
            <a
              href="#"
              className="menu-link px-3"
              onClick={() => handleCompletionStatusFilter("true")}
            >
              Fertige Prozesse
            </a>
          </div>
        </div>

        <div className="menu-item px-3">
          <div className="d-flex flex-row align-items-center gap-3">
            <i
              className="las la-check"
              hidden={
                context.state.paging.filter !== "false" ||
                context.state.paging.filterBy !== "data.content.completed"
              }
            ></i>
            <a
              href="#"
              className="menu-link px-3"
              onClick={() => handleCompletionStatusFilter("false")}
            >
              Laufende Prozesse
            </a>
          </div>
        </div>

        <div className="separator mt-3 opacity-75"></div>

        <div
          className="menu-item px-3"
          data-kt-menu-trigger="click"
          data-kt-menu-placement="right-start"
          data-kt-menu-flip="left-start, top"
        >
          <a href="#" className="menu-link px-3">
            <span className="menu-title">Sortieren</span>
            <span className="menu-arrow"></span>
          </a>

          <div className="menu-sub menu-sub-dropdown w-200px py-4">
            <div className="menu-item px-3">
              <a
                href="#"
                className="menu-link px-3"
                onClick={() => {
                  context.dispatch({
                    type: "SET_PAGE",
                    payload: {
                      take: context.state.itemsPerPage,
                      skip: 0,
                      orderBy: "data.created",
                      order: "desc",
                      filter: context.state.paging.filter,
                      filterBy: context.state.paging.filterBy,
                      filter2: context.state.paging.filter2,
                      filterBy2: context.state.paging.filterBy2,
                    },
                  });
                }}
              >
                <div className="d-flex align-items-center flex-row gap-3">
                  <i
                    className="las la-check"
                    hidden={
                      context.state.paging.order !== "desc" ||
                      context.state.paging.orderBy !== "data.created"
                    }
                  ></i>
                  Datum absteigend
                </div>
              </a>
            </div>

            <div className="menu-item px-3">
              <a
                href="#"
                className="menu-link px-3"
                onClick={() => {
                  context.dispatch({
                    type: "SET_PAGE",
                    payload: {
                      take: context.state.itemsPerPage,
                      skip: 0,
                      orderBy: "data.created",
                      order: "asc",
                      filter: context.state.paging.filter,
                      filterBy: context.state.paging.filterBy,
                      filter2: context.state.paging.filter2,
                      filterBy2: context.state.paging.filterBy2,
                    },
                  });
                }}
              >
                <div className="d-flex align-items-center flex-row gap-3">
                  <i
                    className="las la-check"
                    hidden={
                      context.state.paging.order !== "asc" ||
                      context.state.paging.orderBy !== "data.created"
                    }
                  ></i>
                  Datum aufsteigend
                </div>
              </a>
            </div>
          </div>
        </div>

        <div
          className="menu-item px-3"
          data-kt-menu-trigger="click"
          data-kt-menu-placement="right-start"
          data-kt-menu-flip="left-start, top"
        >
          <a href="#" className="menu-link px-3">
            <span className="menu-title">Prozess Typ</span>
            <span className="menu-arrow"></span>
          </a>

          <div className="menu-sub menu-sub-dropdown w-250px py-4">
            <div className="menu-item px-3">
              <a
                href="#"
                className="menu-link px-3"
                onClick={handleSelectAllTemplates}
              >
                <div className="d-flex align-items-center flex-row gap-3">
                  <i
                    className="las la-check"
                    hidden={context.state.selectedTemplateFilters.length > 0}
                  ></i>
                  Alle Templates
                </div>
              </a>
            </div>
            
            <div className="menu-item px-3">
              <a
                href="#"
                className="menu-link px-3"
                onClick={handleDeselectAllTemplates}
              >
                <div className="d-flex align-items-center flex-row gap-3">
                  <i className="las la-times text-danger"></i>
                  Alle abwählen
                </div>
              </a>
            </div>
            
            <div className="separator mt-2 mb-2 opacity-50"></div>
            
            {context.state.templates.map((template, index) => {
              // Exclusion logic: unchecked templates are excluded from results
              // When no templates are excluded, show all as checked (meaning all are included)
              // When specific templates are excluded, show those as unchecked
              const isExcluded = context.state.selectedTemplateFilters.includes(template.data?.name || "");
              const isSelected = !isExcluded; // Inverted logic for exclusion
              
              return (
                <div className="menu-item px-3" key={'processFilterItem' + index}>
                  <a
                    href="#"
                    className="menu-link px-3"
                    onClick={() => handleTemplateFilterToggle(template.data?.name || "")}
                  >
                    <div className="d-flex align-items-center flex-row gap-3">
                      <div className="form-check form-check-sm">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}} // Handled by onClick above
                        />
                      </div>
                      <span className={isSelected ? "fw-bold" : ""}>{template.data?.name}</span>
                    </div>
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
